/*
 * Copyright (c) 2025. Orion Analytics.
 */

import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";
import { fetchSectorPerformance } from "../../api";

const SectorPerformance = () => {
    const [data, setData] = useState([]);

    useEffect(() => {
        fetchSectorPerformance()
            .then((data) => setData(data))
            .catch((error) =>
                console.error(
                    "Error fetching sector performance:",
                    error
                )
            );
    }, []);

    if (!data.length) {
        return (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                Loading sector performance...
            </div>
        );
    }

    const sectors = data.map(
        (item) => item.sector_symbol
    );

    const change1D = data.map(
        (item) => item.change_1d
    );

    const changeYTD = data.map(
        (item) => item.change_ytd
    );

    const colors = changeYTD.map((val) =>
        val >= 0 ? "#10b981" : "#ef4444"
    );

    const bestSector = [...data].sort(
        (a, b) => b.change_ytd - a.change_ytd
    )[0];

    const worstSector = [...data].sort(
        (a, b) => a.change_ytd - b.change_ytd
    )[0];

    return (
        <div className="space-y-8">

            {/* HEADER */}

            <div>
                <h2 className="text-2xl font-bold text-slate-900">
                    Sector Performance
                </h2>

                <p className="text-slate-600 mt-1">
                    Sector rotation, momentum and
                    relative strength analysis
                </p>
            </div>

            {/* SUMMARY CARDS */}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                    <p className="text-sm text-slate-500">
                        Best Performing Sector
                    </p>

                    <h3 className="text-xl font-bold text-emerald-600 mt-2">
                        {bestSector.sector_symbol}
                    </h3>

                    <p className="text-lg font-semibold">
                        {bestSector.change_ytd.toFixed(2)}%
                    </p>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                    <p className="text-sm text-slate-500">
                        Worst Performing Sector
                    </p>

                    <h3 className="text-xl font-bold text-red-600 mt-2">
                        {worstSector.sector_symbol}
                    </h3>

                    <p className="text-lg font-semibold">
                        {worstSector.change_ytd.toFixed(2)}%
                    </p>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                    <p className="text-sm text-slate-500">
                        Total Sectors Tracked
                    </p>

                    <h3 className="text-3xl font-bold text-slate-800 mt-2">
                        {data.length}
                    </h3>
                </div>

            </div>

            {/* CHARTS */}

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">

                    <h3 className="font-semibold mb-4">
                        YTD Sector Returns
                    </h3>

                    <Plot
                        data={[
                            {
                                x: sectors,
                                y: changeYTD,
                                type: "bar",
                                marker: {
                                    color: colors,
                                },
                            },
                        ]}
                        layout={{
                            autosize: true,
                            height: 450,
                            margin: {
                                l: 50,
                                r: 20,
                                t: 30,
                                b: 100,
                            },
                        }}
                        style={{
                            width: "100%",
                        }}
                        config={{
                            responsive: true,
                        }}
                    />
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">

                    <h3 className="font-semibold mb-4">
                        Sector Heatmap
                    </h3>

                    <Plot
                        data={[
                            {
                                x: sectors,
                                y: ["Return"],
                                z: [changeYTD],
                                type: "heatmap",
                                colorscale: [
                                    ["0", "#ef4444"],
                                    ["0.5", "#facc15"],
                                    ["1", "#10b981"],
                                ],
                            },
                        ]}
                        layout={{
                            autosize: true,
                            height: 450,
                            margin: {
                                l: 50,
                                r: 20,
                                t: 30,
                                b: 100,
                            },
                        }}
                        style={{
                            width: "100%",
                        }}
                        config={{
                            responsive: true,
                        }}
                    />
                </div>

            </div>

            {/* TABLE */}

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">

                <div className="px-6 py-4 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-900">
                        Sector Details
                    </h3>
                </div>

                <div className="overflow-x-auto">

                    <table className="w-full">

                        <thead className="bg-slate-50">

                            <tr>

                                <th className="px-4 py-3 text-left text-sm font-semibold">
                                    Sector
                                </th>

                                <th className="px-4 py-3 text-right text-sm font-semibold">
                                    1 Day %
                                </th>

                                <th className="px-4 py-3 text-right text-sm font-semibold">
                                    YTD %
                                </th>

                                <th className="px-4 py-3 text-left text-sm font-semibold">
                                    Top Gainers
                                </th>

                                <th className="px-4 py-3 text-left text-sm font-semibold">
                                    Top Losers
                                </th>

                            </tr>

                        </thead>

                        <tbody>

                            {data.map((sector, idx) => (

                                <tr
                                    key={idx}
                                    className="
                                        border-b
                                        border-slate-100
                                        hover:bg-slate-50
                                    "
                                >

                                    <td className="px-4 py-3 font-medium">
                                        {sector.sector_symbol}
                                    </td>

                                    <td
                                        className={`px-4 py-3 text-right font-semibold ${
                                            sector.change_1d >= 0
                                                ? "text-emerald-600"
                                                : "text-red-600"
                                        }`}
                                    >
                                        {sector.change_1d}%
                                    </td>

                                    <td
                                        className={`px-4 py-3 text-right font-semibold ${
                                            sector.change_ytd >= 0
                                                ? "text-emerald-600"
                                                : "text-red-600"
                                        }`}
                                    >
                                        {sector.change_ytd}%
                                    </td>

                                    <td className="px-4 py-3 text-sm">
                                        {
                                            sector.top_gainers
                                        }
                                    </td>

                                    <td className="px-4 py-3 text-sm">
                                        {
                                            sector.top_losers
                                        }
                                    </td>

                                </tr>

                            ))}

                        </tbody>

                    </table>

                </div>

            </div>

        </div>
    );
};

export default SectorPerformance;